/**
 * DEPLOY_ENV — which deployment an app process is running in.
 *
 * ## Why this exists: `NODE_ENV` is a BUILD flag, not a deployment flag
 *
 * `NODE_ENV` answers "how was this bundle compiled?" — `production` means
 * minified, `development` means dev-server/HMR. Every Render service we run
 * (including every staging and demo environment) builds with
 * `NODE_ENV=production`, because we always want optimized output. So
 * `NODE_ENV` is IDENTICAL on real production and on staging, and reading it to
 * decide "am I in production?" is always wrong in a deployed environment.
 *
 * `DEPLOY_ENV` answers the separate question "which environment is this?" and
 * is the ONLY variable that should gate deployment-sensitive behavior
 * (destructive actions, live payments, live EDI submissions, prod-only banners).
 *
 * | question                         | variable     | prod value   | staging value |
 * | -------------------------------- | ------------ | ------------ | ------------- |
 * | how was the bundle compiled?     | `NODE_ENV`   | `production` | `production`  |
 * | which deployment is this?        | `DEPLOY_ENV` | `production` | `staging`     |
 *
 * The frontend receives the same value as `REACT_APP_DEPLOY_ENV`. It is
 * injected into `build/index.html` AFTER the bundle is built (see
 * `frontend/scripts/inject-env.js`), so its value is deliberately NOT part of
 * the Turborepo `build-base` cache key — every environment reuses one cached
 * frontend build.
 *
 * ## Demos are NOT `production`
 *
 * Demo environments (Demo (Praxis), Demo (Pillar), Rexel Demo, Rebates Demo)
 * are `demo`, never `production` — even though several of them are wired to the
 * PRODUCTION Sentry DSN via the `prod-frontend-base` env-var group, because we
 * want demo crashes triaged with the same urgency as real ones.
 *
 * Sentry grouping and `DEPLOY_ENV` are independent on purpose:
 *
 * | environment    | Sentry DSN | `DEPLOY_ENV` |
 * | -------------- | ---------- | ------------ |
 * | Pennrose Prod  | prod       | `production` |
 * | Demo (Praxis)  | prod       | `demo`       |
 * | Rexel Demo     | staging    | `demo`       |
 * | Pennrose Stg   | staging    | `staging`    |
 *
 * So never infer one from the other: "shares the production Sentry project"
 * does not mean "is a production deployment". Only `production` carries real
 * customer data and real side effects.
 */
export const DEPLOY_ENVS = [
  /** A developer's machine (`pnpm dev`) — the default when DEPLOY_ENV is unset. */
  "local",
  /** Automated test runs (vitest, CI). */
  "test",
  /** Pre-production environment tracking `main`. Real integrations, throwaway data. */
  "staging",
  /**
   * Customer-facing demo/sales environment. Treated as production for Sentry
   * (see above) but NOT for anything gated on `isProductionDeployEnv`.
   */
  "demo",
  /** Internal Gently scratch environment. */
  "sandbox",
  /** Real production: real customer data, real side effects. */
  "production",
] as const;

export type DeployEnv = (typeof DEPLOY_ENVS)[number];

/** Default when `DEPLOY_ENV` is unset — assume the least dangerous environment. */
export const DEFAULT_DEPLOY_ENV: DeployEnv = "local";

export function isDeployEnv(value: unknown): value is DeployEnv {
  return (
    typeof value === "string" &&
    (DEPLOY_ENVS as readonly string[]).includes(value)
  );
}

/**
 * Parse an untrusted `DEPLOY_ENV` value, falling back to `local`.
 *
 * Fails safe: an unrecognized or missing value degrades to `local` rather than
 * to `production`, so a typo can never silently unlock production behavior.
 */
export function parseDeployEnv(value: unknown): DeployEnv {
  return isDeployEnv(value) ? value : DEFAULT_DEPLOY_ENV;
}

/**
 * The single check for "is this a real production deployment?".
 *
 * Deliberately excludes `demo` — see the note on demos above.
 */
export function isProductionDeployEnv(deployEnv: DeployEnv): boolean {
  return deployEnv === "production";
}

/**
 * "Is this a deployed environment whose data is throwaway?" — the environments
 * where real integrations run but nothing users do here is real.
 *
 * Deliberately excludes `demo`: demo environments are customer-facing sales
 * surfaces, and a TEST ENVIRONMENT banner across the top of a demo would
 * undermine the demo. `local`/`test` are excluded too — a developer running
 * `pnpm dev` does not need to be told.
 */
export function isTestDeployEnv(deployEnv: DeployEnv): boolean {
  return deployEnv === "staging" || deployEnv === "sandbox";
}
